import { ArrowRight, Play } from 'lucide-react';
import { Button } from './ui/button';
import Starfield from './Starfield';
import BrandLogo from './BrandLogo';
import { GoogleIcon } from './BrandIcons';

/**
 * OPTION A — "Editorial Studio"
 * Clean, ultra-minimal, luxury/Apple-editorial. Big centered typography,
 * one dominant cinematic image, lots of breathing room, subtle motion.
 */
export default function HeroEditorial({ onSignIn }) {
  const startGoogleAuth = () => {
    const redirectUrl = window.location.origin + '/build';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <section className="relative min-h-screen text-white overflow-hidden bg-[#040611]">
      <div className="absolute inset-0">
        <Starfield density={100} />
      </div>
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(1200px 800px at 50% 15%, rgba(99,102,241,0.28) 0%, transparent 55%),' +
            'radial-gradient(900px 600px at 50% 95%, rgba(192,38,211,0.18) 0%, transparent 60%)',
        }}
      />

      <div className="relative max-w-5xl mx-auto px-6 pt-40 text-center">
        <div className="mb-10 flex justify-center">
          <BrandLogo size={36} tone="light" />
        </div>

        <div className="text-[10px] tracking-[0.5em] uppercase text-white/45 font-semibold mb-8">
          Introducing &mdash; Around the World AI
        </div>

        <h1 className="text-6xl md:text-8xl lg:text-[112px] font-black tracking-[-0.055em] leading-[0.9]">
          <span className="block">The world&rsquo;s</span>
          <span className="block italic bg-clip-text text-transparent bg-gradient-to-br from-cyan-200 via-indigo-300 to-fuchsia-300">
            AI dev studio.
          </span>
        </h1>

        <p className="mt-10 mx-auto max-w-xl text-slate-300/90 text-lg leading-relaxed">
          One conversation. A full-stack production app.
          <br />
          Shipped to a global CDN in minutes.
        </p>

        <div className="mt-12 flex items-center justify-center gap-4">
          <Button
            data-testid="editorial-cta"
            onClick={startGoogleAuth}
            className="h-13 rounded-full bg-white hover:bg-slate-100 text-slate-900 text-[15px] font-bold px-7 gap-2"
          >
            <GoogleIcon size={18} />
            Start building &nbsp; <ArrowRight size={16} />
          </Button>
          <button
            onClick={onSignIn}
            data-testid="editorial-watch"
            className="text-sm text-white/70 hover:text-white transition flex items-center gap-2"
          >
            <span className="h-8 w-8 rounded-full border border-white/30 flex items-center justify-center">
              <Play size={11} fill="white" className="ml-0.5" />
            </span>
            Watch the film
          </button>
        </div>

        {/* One dominant cinematic image */}
        <div className="mt-24 relative">
          <div
            className="relative rounded-[32px] overflow-hidden border border-white/10 aspect-[16/9] max-w-4xl mx-auto"
            style={{
              boxShadow:
                '0 80px 160px -40px rgba(99,102,241,0.5), 0 30px 80px -30px rgba(0,0,0,0.8)',
            }}
          >
            <img
              src="https://images.unsplash.com/photo-1614730321146-b6fa6a46bcb4?w=1800&q=90&auto=format&fit=crop"
              alt="Earth from space"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div
              className="absolute inset-0 mix-blend-multiply"
              style={{
                background:
                  'linear-gradient(135deg, rgba(6,182,212,0.25), rgba(99,102,241,0.3) 45%, rgba(192,38,211,0.2))',
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, rgba(3,7,18,0) 60%, rgba(3,7,18,0.85) 100%)',
              }}
            />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="text-[10px] tracking-[0.4em] uppercase text-cyan-200 font-bold mb-2">
                One planet, one platform
              </div>
              <div className="text-2xl md:text-3xl font-black tracking-tight text-white max-w-md">
                Build for anyone, anywhere &mdash; from your browser.
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-center gap-8 text-white/40 text-[11px] tracking-[0.3em] uppercase font-semibold">
          <span>Claude 4.5</span>
          <span>&middot;</span>
          <span>GPT-5</span>
          <span>&middot;</span>
          <span>Gemini 2.5</span>
          <span>&middot;</span>
          <span>Stripe</span>
          <span>&middot;</span>
          <span>MongoDB</span>
        </div>
      </div>
    </section>
  );
}
